import { Component, OnInit, inject } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { CurrentAddress } from 'src/providers/addresses/current-address';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { TasksService } from 'src/providers/tasks/tasks';
import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';
import { Integrations } from 'src/providers/integrations/integrations';
import { Client } from 'src/providers/client/client';
import { CustomFieldsPage } from 'src/pages/more/custom-fields/custom-fields';
import { AddCustomFieldPage } from 'src/pages/integrations/integration-settings/add-custom-field/add-custom-field';

import { Loading } from 'src/shared/components/loading/loading';
import { Genome } from 'src/providers/genome/genome';
import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';
import { PhotoNotes } from 'src/providers/photo-notes/photo-notes';
import { Aws } from 'src/providers/aws/aws';
import { CameraProvider } from 'src/shared/providers/camera/camera';
import { Util } from 'src/shared/util/util';
import { AssetsQueriesService } from 'src/shared/services/assets-queries.service';

import { EditRoomItemPage } from 'src/pages/more/edit-address/room-item-details/edit-room-item/edit-room-item';
import { environment } from 'src/environments/environment';
import { WindowService } from 'src/shared/providers/window.service';

import { RestockingOrdersService, RestockOrderState } from 'src/shared/services/restocking-orders.service';
import { formatMoney, stateLabel } from 'src/shared/services/restock-order-state.pipe';

import { PreferredBrand, asHttpUrl, packLabel, unitsPerPackFor } from 'src/models/restocking-brand.model';

// "Current Order" card: the item's slice of the newest in-flight restock order,
// precomputed so the template stays declarative (no function calls).
interface CurrentOrderVM {
  orderId: number;
  chipClass: string;
  chipLabel: string;
  productLine: string;    // 'Bounty · Select-A-Size, 6 rolls'
  // Set when the resolved product is a raw vendor URL rather than a brand name, so
  // the line can open the product page instead of the order.
  productUrl: string;
  quantityLine: string;   // '3 packs × $5.99'
  etaText: string;        // 'Arrives ~Aug 4'
  substitutionNote?: string;
  steps: { label: string; stepClass: string }[];
}

@Component({
    templateUrl: 'room-item-details.html',
    standalone: false
})

export class ItemDetailsPage implements OnInit {
  private readonly assetsQueries = inject(AssetsQueriesService);

  addressId: number;
  boundSendAttachment: Function;
  boundSendNote: Function;
  currentUserId: number;
  currentOrder: CurrentOrderVM | null = null;
  customBack: string;
  customFields: any[] = [];
  displayBrands: PreferredBrand[] = [];
  preferredBrandsSummary = '';
  dialogParams: any;
  errorMessage: string;
  hasQuantityChange: boolean;
  quantityChanges: { key: string; title: string; subtitle: string }[] = [];
  // Placeholder detail rows shown while the item loads — roughly what a consumable
  // renders (inventory, year, min/max, opportunistic, price, notes, timing).
  readonly skeletonDetailRows = [0, 1, 2, 3, 4, 5];
  integrationDetails: any = null;
  inventorySetting: string;
  usedPerReservation: number;
  isRightSideContent: boolean;
  isSendingNote = false;
  item: any;
  loaded: boolean;
  noteForm: UntypedFormGroup;
  notes: any[] = [];
  pastMaintenances: any;
  showTasksDropdown: boolean;
  upcomingMaintenances: any;
  warehouseItem: any;

  constructor(
    private aws: Aws,
    private camera: CameraProvider,
    private client: Client,
    private currentAddress: CurrentAddress,
    private fb: UntypedFormBuilder,
    private genome: Genome,
    private guestReservations: GuestReservations,
    private http: HttpClient,
    private integrations: Integrations,
    private modalController: ModalController,
    private navCtrl: CustomNavController,
    private photoNotes: PhotoNotes,
    private restockingOrders: RestockingOrdersService,
    private rightSidePanelService: RightSidePanelService,
    private route: ActivatedRoute,
    private storage: TidyStorage,
    public tasksService: TasksService,
    private util: Util,
    private windowService: WindowService
  ) {
    this.noteForm = this.fb.group({
      note: ['']
    });
    this.boundSendNote = this.sendNote.bind(this);
    this.boundSendAttachment = this.sendAttachment.bind(this);
  }

  @Loading('', true)
  async ngOnInit() {
    try {
      this.loaded = false;
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      if (this.isRightSideContent) {
        this.dialogParams = await this.storage.retrieve('dialog-params');
        this.rightSidePanelService.setDialogPageTitle('Item');
      }
      const me = await this.client.getMe();
      this.currentUserId = me?.current_user?.id;
      this.addressId = this.navCtrl.getParam('addressId') || this.dialogParams.addressId;
      const itemId = parseInt(this.route.snapshot.paramMap.get('itemId')) || this.dialogParams.itemId;
      this.item = await this.genome.getRoomObject(itemId);
      // Hidden with the item page's Custom Fields section until the server can save item values.
      // this.customFields = await this.getCustomFields(this.item);
      this.showTasksDropdown = true;
      this.checkIfHasQuantityChangeHistory();
      this.customBack = localStorage.getItem('roomItemBackPage') || `properties?propertyId=${this.addressId}`;
      const [upcomingMaintenances, pastMaintenances] = await this.getMaintenances(this.addressId);
      this.upcomingMaintenances = upcomingMaintenances;
      this.pastMaintenances = pastMaintenances;
      this.buildItemNames();
      await this.loadPreferredBrandsDisplay();
      await this.loadIntegrationDetails();
      await this.loadWarehouseDetails();
      this.inventorySetting = this.buildInventorySettings();
      this.usedPerReservation = this.item.inventory_settings?.strategies?.find((strategy) => strategy.type === 'auto')?.amount_per_reservation ?? null;
      await this.loadCurrentOrder();
      await this.loadNotes();
      this.loaded = true;
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  // Builds the Change History rows as two finished sentences. They used to be
  // assembled from seven <tidy-text> fragments glued together with &nbsp; spans,
  // which stacked an nbsp on top of the row's 4px inter-element margin and left a
  // visible double space around every value.
  checkIfHasQuantityChangeHistory() {
    const translate = new TranslationPipe();
    this.quantityChanges = (this.item.change_history || [])
      .filter((history) => history?.data?.current_quantity)
      .map((history, index) => {
        const [from, to] = history.data.current_quantity;
        return {
          key: `${history?.local_change_date_and_time?.date || ''}-${index}`,
          title: `${translate.transform('Quantity Change From')} ${from} ${translate.transform('to')} ${to}`,
          subtitle: `${translate.transform('Made by')} ${this.getRecordedByName(history)} ${translate.transform('on')} ${this.formatDate(history?.local_change_date_and_time?.date)}`
        };
      });
    this.hasQuantityChange = this.quantityChanges.length > 0;
  }

  async getMaintenances(addressId) {
    const upcomingMaintenances = this.genome.getUpcomingMaintenancesByRoomObject(addressId, this.item.id);
    const pastMaintenances = this.genome.getPastMaintenancesByRoomObject(addressId, this.item.id);
    return await Promise.all([upcomingMaintenances, pastMaintenances])
  }

  buildItemNames() {
    this.item.model.model = this.item.model.model === null ? 'No Model Specified' : this.item.model.model;
    this.item.model.name = this.item.model.name === null ? 'No Model Specified' : this.item.model.name;
  }

  // Read-only brand waterfall shown on this page. Mirrors the edit page: use the
  // item's saved brands, otherwise fall back to the category defaults so the
  // waterfall is visible even before anything is saved.
  async loadPreferredBrandsDisplay() {
    const persisted = (this.item?.preferred_brands || []).map((brand) => ({ ...brand }));
    if (persisted.length) {
      this.displayBrands = persisted;
    } else {
      const categoryId = this.item?.model?.category?.id;
      this.displayBrands = await this.resolveDefaultPreferredBrands(categoryId);
    }
    // Single wrapping line: "Brand 1 → Brand 2 → Brand 3" (top 3 tiers).
    this.preferredBrandsSummary = this.displayBrands
      .slice(0, 3)
      .map((brand) => brand.brand)
      .join(' → ');
  }

  async resolveDefaultPreferredBrands(categoryId): Promise<PreferredBrand[]> {
    if (!categoryId) {
      return [];
    }
    try {
      const apiDefaults = await this.genome.getPreferredBrandDefaults(categoryId);
      return (apiDefaults || []).map((brand, index) => ({ ...brand, position: index + 1 }));
    } catch (_err) {
      return [];
    }
  }

  // Loads the item's slice of its newest in-flight restock order (preview →
  // delivered) into the "Current Order" card. Non-blocking: any failure just
  // means the card doesn't render, the rest of the page is unaffected.
  async loadCurrentOrder() {
    this.currentOrder = null;
    if (!this.item?.consumable) {
      return;
    }
    try {
      const addressId = this.item.address_id || this.addressId;
      await this.restockingOrders.loadOrders(addressId);
      const match = this.restockingOrders.getCurrentOrderForRoomObject(this.item.id, addressId);
      if (!match) {
        return;
      }
      const { order, item } = match;
      const resolved = item.resolved;
      const productLine = [resolved?.brand, resolved?.productName].filter(Boolean).join(' · ');
      const packs = packLabel(item.orderQuantity, unitsPerPackFor(resolved, this.item));
      this.currentOrder = {
        orderId: order.id,
        chipClass: `restock-status-chip ${order.state} restock-current-order-chip`,
        chipLabel: stateLabel(order.state),
        productLine,
        productUrl: asHttpUrl(resolved?.productName),
        quantityLine: resolved?.unitPrice ? `${packs} × ${formatMoney(resolved.unitPrice)}` : packs,
        etaText: order.state === 'ordered' && order.eta ? `Arrives ~${this.formatDate(order.eta)}` : '',
        substitutionNote: resolved?.substitutionNote,
        steps: this.buildOrderSteps(order.state)
      };
    } catch (_err) {
      // Card is optional — the item page must not fail because orders didn't load.
    }
  }

  // Same four steps and state mapping as the order-detail timeline, so the item
  // card and the order page never disagree about where an order is.
  private buildOrderSteps(state: RestockOrderState): { label: string; stepClass: string }[] {
    const labels = ['Concierge Review', 'Order Placed', 'Shipment Delivered', 'Items Restocked'];
    let currentIndex: number;
    switch (state) {
      case 'preview':
      case 'finalizing':
        currentIndex = 0;
        break;
      case 'ordered':
        currentIndex = 2;
        break;
      case 'delivered':
        currentIndex = 3;
        break;
      case 'restocked':
        currentIndex = labels.length;
        break;
      default:
        currentIndex = -1;
    }
    return labels.map((label, index) => ({
      label,
      stepClass: `restock-item-step ${
        currentIndex !== -1 && index < currentIndex ? 'done' : index === currentIndex ? 'current' : ''
      }`.trim()
    }));
  }

  goToCurrentOrder() {
    if (!this.currentOrder) {
      return;
    }
    // Resolve RestockOrderPage from the (already-eager) route registry rather
    // than importing it here: a static/dynamic import edge from this page —
    // eagerly evaluated via shared.module → dynamic-dialog-container — threads a
    // new cycle through app.module and reorders module init, breaking unrelated
    // base classes on boot ("Class extends value undefined"). navigateTo then
    // handles the desktop panel vs mobile push, same as goToWarehouseItem.
    const orderId = this.currentOrder.orderId;
    const component = this.rightSidePanelService.getComponentFromRoute('restock-order');
    this.rightSidePanelService.navigateTo(`restock-order/${orderId}`, { orderId }, component);
  }

  // tidy-text stops propagation on its own, so this opens the vendor page without
  // also firing the card's "go to order" action.
  openProductUrl() {
    if (this.currentOrder?.productUrl) {
      this.util.openUrl(this.currentOrder.productUrl);
    }
  }

  goToEditItem() {
    if (!this.item.consumable) {
      const params = {
        item: this.item,
        addressId: this.addressId
      };
      this.goToPage(`edit-room-item`, params, EditRoomItemPage);
    } else {
      const params = {
        item: this.item,
        addressId: this.item.address_id,
        showConsumables: this.item.consumable
      };
      this.rightSidePanelService.navigateTo('add-item-form', params);
    }
  }

  @Loading('', true)
  async deleteItem() {
    const params: ConfirmPageParamsModel = {
      title: 'Delete item?',
      body: `Are you sure you want to delete ${this.item.model.name}?`,
      backText: 'Go Back',
      confirmText: 'Delete Item',
      confirmAction: this.confirmDeleteItem.bind(this)
    };
    const confirmationModal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    confirmationModal.present();
  }

  async confirmDeleteItem() {
    try {
      await this.genome.inactivateRoomObject(this.item.id);
      this.assetsQueries.invalidateAssets();
      let route = this.navCtrl.getParam('cameFromRoute') || this.dialogParams?.cameFromRoute;
      if (!route) {
        route = this.item?.consumable ? 'restocking' : 'assets';
      }
      if (this.isRightSideContent) {
        await this.storage.save('shouldReloadPageAfterClosingSideRail', true);
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateForward(route);
      }
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    } finally {
      this.modalController.dismiss();
    }
  }

  formatDate(date) {
    return new ParseDateTimePipe().transform(date, 'MMM d');
  }

  async goToPage(url, params, component) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        return this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

  getCharacteristics(characteristics) {
    return characteristics.map((characteristic) => characteristic.name).join(' / ');
  }

  buildInventorySettings() {
    return this.item.inventory_settings?.strategies?.map((strategy) => {
      if (strategy.type === 'auto') {
        return 'Automatically';
      } else if (strategy.type === 'task') {
        return 'Using checklists';
      }
    }).join(', ');
  }

  async loadIntegrationDetails() {
    try {
      const whereToOrder = this.item?.where_to_order;
      if (whereToOrder && typeof whereToOrder === 'string' && whereToOrder.startsWith('integration:')) {
        const integrationId = whereToOrder.replace('integration:', '');
        const userSources = await this.guestReservations.getReservationSource();
        const matchingSource = userSources.find(s => s.id.toString() === integrationId);
        if (matchingSource) {
          this.integrationDetails = {
            name: matchingSource.guest_reservation_source_type?.name,
            logo_url: matchingSource.guest_reservation_source_type?.logo_url
          };
        }
      }
    } catch (error) {
      // If error, just show the original value
    }
  }

  async loadWarehouseDetails() {
    try {
      if (this.item?.warehouse_room_object_id) {
        this.warehouseItem = await this.genome.getRoomObject(this.item.warehouse_room_object_id);
      }
    } catch (error) {
      // If error, warehouse link won't be shown
    }
  }

  goToWarehouseItem() {
    if (this.warehouseItem) {
      const params = {
        addressId: this.warehouseItem.address_id,
        itemId: this.warehouseItem.id
      };
      this.rightSidePanelService.navigateTo(`item-details/${this.warehouseItem.id}`, params, ItemDetailsPage);
    }
  }

  async getCustomFields(item: any) {
    try {
      const userCustomFields = await this.integrations.getCustomFields('RoomObject');
      const customFields = item?.custom_fields;
      if (!customFields) {
        return [];
      }
      return Object.keys(customFields).map((key) => {
        const userCustomField = userCustomFields.find((field) => field.field_key === key);
        return {
          name: userCustomField?.field_name || key,
          value: customFields[key] || 'N/A',
        };
      });
    } catch (_err) {
      return [];
    }
  }

  editCustomFields() {
    this.rightSidePanelService.navigateTo('custom-fields', { modelType: 'RoomObject', instance: this.item }, CustomFieldsPage);
  }

  getInstallationYear(installationDate: string): string {
    if (!installationDate) {
      return '';
    }
    const parts = installationDate.split('-');
    if (parts.length >= 1) {
      return parts[0];
    }
    return '';
  }

  getRecordedByName(history: any): string {
    return history?.recorded_by?.first_name || 'Guest reservation';
  }

  async loadNotes() {
    try {
      const url = `${environment.api_url}api/v1/customer/room-objects/${this.item.id}?includes=text_notes,photo_notes`;
      const response: any = await firstValueFrom(this.http.get(url));
      this.notes = [];
      if (response?.text_notes) {
        response.text_notes.forEach((note: any) => {
          this.notes.push({
            id: note.id,
            type: 'text',
            content: note.body,
            created_at: note.created_at,
            user: this.getNoteUserDisplay(note)
          });
        });
      }
      if (response?.photo_notes) {
        response.photo_notes.forEach((photo: any) => {
          this.notes.push({
            id: photo.id,
            type: 'photo',
            content: photo.photo_url,
            created_at: photo.created_at,
            user: this.getNoteUserDisplay(photo)
          });
        });
      }
      this.notes.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    } catch (err) {
      console.error('Error loading notes:', err);
    }
  }

  private getNoteUserDisplay(note: any): string {
    if (note.created_by_type === 'CustomerMember' && note.created_by_id === this.currentUserId) {
      return 'You';
    }
    if (note.user?.first_name && note.user?.last_name) {
      return `${note.user.first_name} ${note.user.last_name}`;
    }
    if (note.user?.first_name) {
      return note.user.first_name;
    }
    return 'User';
  }

  async sendNote() {
    const noteText = this.noteForm.get('note').value;
    if (!noteText || !noteText.trim()) {
      return;
    }
    this.isSendingNote = true;
    try {
      const url = `${environment.api_url}api/v1/customer/text-notes`;
      const payload = {
        text_notes: [{
          type: 'room_object',
          object_id: this.item.id,
          body: noteText.trim()
        }]
      };
      const response: any = await firstValueFrom(this.http.post(url, payload));
      const addedNotes = Array.isArray(response) ? response : (response?.text_notes || [response]);
      if (addedNotes.length > 0) {
        const newNote = addedNotes[0];
        this.notes.push({
          id: newNote.id,
          type: 'text',
          content: newNote.body,
          created_at: newNote.created_at,
          user: 'You'
        });
        this.notes.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      }
      this.noteForm.reset();
    } catch (err) {
      this.util.showError('Failed to add note. Please try again.');
      console.error('Error sending note:', err);
    } finally {
      this.isSendingNote = false;
    }
  }

  async sendAttachment() {
    try {
      const picture = await this.camera.getImg(true);
      if (!picture) {
        return;
      }
      const photoKey = `room-object/${this.item.id}/${Date.now()}`;
      const awsResp = await this.aws.uploadFileToS3(picture.dataUrl, photoKey, 'image/jpeg');
      const url = `${environment.api_url}api/v1/customer/photo-notes`;
      const payload = {
        photo_notes: [{
          type: 'room_object',
          object_id: this.item.id,
          photo_url: awsResp.Location,
          s3_photo_url_key: awsResp.Key
        }]
      };
      const response: any = await firstValueFrom(this.http.post(url, payload));
      const addedPhotos = Array.isArray(response) ? response : (response?.photo_notes || [response]);
      if (addedPhotos.length > 0) {
        const newPhoto = addedPhotos[0];
        this.notes.push({
          id: newPhoto.id,
          type: 'photo',
          content: newPhoto.photo_url,
          created_at: newPhoto.created_at,
          user: 'You'
        });
        this.notes.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      }
    } catch (err) {
      this.util.showError('Failed to upload photo. Please try again.');
      console.error('Error sending attachment:', err);
    }
  }

  addCustomField() {
    this.rightSidePanelService.navigateTo('integration/add-custom-field', { modelType: 'RoomObject' }, AddCustomFieldPage);
  }

}
